'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { isPublicUserIdentityField, usePublicUserIdentity } from '../hooks/usePublicUserIdentity.js';
import { stripPublicUserIdentityFields } from '../internal/public-user-entity.js';
import { createDetailFieldHook } from './createDetailFieldHook.js';
import { createHook } from './createHook.js';
export function createPublicUserHook(spec, realtime) {
  const useBase = createHook(spec, realtime);
  return function usePublicUserHook(args, opts) {
    const result = useBase(args, opts);
    const queryClient = useQueryClient();
    const factory = spec.entityKeys[spec.kind];
    const queryKey = factory?.(args).queryKey ?? [];
    const identifier = readIdentifier(args);
    const data = usePublicUserIdentity(result.data, identifier === undefined ? [] : [identifier]);
    useEffect(() => {
      if (result.data === null || typeof result.data !== 'object') return;
      const projection = stripPublicUserIdentityFields(result.data);
      if (projection !== result.data) queryClient.setQueryData(queryKey, projection);
    }, [queryClient, queryKey, result.data]);
    return new Proxy(result, {
      get(target, property, receiver) {
        return property === 'data' ? data : Reflect.get(target, property, receiver);
      }
    });
  };
}
export function createPublicUserDetailFieldHook(spec) {
  const useBase = createDetailFieldHook(spec);
  return function usePublicUserDetailField(args, field) {
    const value = useBase(args, field);
    const identifier = readIdentifier(args);
    const identity = usePublicUserIdentity({
      [field]: value
    }, identifier === undefined ? [] : [identifier]);
    return isPublicUserIdentityField(field) ? identity[field] : value;
  };
}
function readIdentifier(args) {
  const value = args.identifier;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}